import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Building2, DoorOpen, Volume2, Truck, PawPrint, Users, Briefcase, Thermometer, Sun,
} from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CtaBlock } from "../components/CtaBlock";
import { FaqAccordion, buildFaqLd } from "../components/FaqAccordion";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";
import { Picture, findPhoto } from "../lib/photos";
import { useSeo } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPARE_ROWS = [
  ["Entry style", "Enter through the lobby, take the interior hallway to your door.", "Park at your door — open the door, you're inside."],
  ["Noise level", "Quieter — the inner hallway insulates the room from lot activity.", "Slightly livelier — you'll hear the parking lot and, at times, road."],
  ["Temperature", "Climate-controlled hallway keeps rooms warmer in winter.", "Direct outside access means winter air enters when the door opens."],
  ["Distance to pool", "Steps down the hall.", "A short walk across the lot or through a lobby entry."],
  ["Distance to breakfast", "Steps down the hall — no jacket needed.", "A short walk over to the interior side."],
  ["Truck / large-vehicle access", "Park in the main lot, walk in with your gear.", "Park within arm's reach of the door — best for loading and unloading."],
  ["Pet convenience", "Walk your pet out through the lobby (or a nearby side exit).", "Straight out the door — very popular with dog owners."],
  ["Late-night arrival", "Fine — the lobby is staffed 24/7.", "Best — key-in-door with no walk through the lobby."],
  ["Best for light sleepers", "Yes — noticeably quieter.", "Consider interior instead if noise is a factor."],
  ["Number of rooms", "22 rooms.", "30 rooms."],
];

const GUIDE_FAQS = [
  { q: "Which is cheaper — interior or exterior?", a: "The rate depends on the room layout, not just the corridor. Some exterior rooms are our best price; some interior Kings are similarly priced. Call the front desk for our best direct rate for the dates you need." },
  { q: "Are exterior rooms less clean or lower quality?", a: "No. Every room at Westbridge — interior or exterior — gets the same daily housekeeping, the same standard amenities, and the same fridge, microwave, coffee maker, and 55-inch 4K TV. The difference is entry style, not quality." },
  { q: "I'm a light sleeper. Which should I pick?", a: "Interior. The climate-controlled hallway insulates the room from lot noise, weather, and door slams. Ask at booking or check-in to be placed away from the elevator and ice machine." },
  { q: "I have a big truck. Which should I pick?", a: "Exterior. You'll park within arm's reach of your room door, load and unload without walking through the lobby, and be first out at 5 a.m. Ask for driver rates when you call." },
  { q: "I'm traveling with a dog. Which should I pick?", a: "Exterior is easier for bathroom breaks, but both work. All Westbridge rooms are pet-friendly, up to 2 pets per room, with a small fee at check-in." },
  { q: "I'm coming in winter. Which should I pick?", a: "Interior. Colder mornings and snow make interior corridors more comfortable. You can walk from your door to the breakfast room to the pool without a jacket." },
  { q: "Can I switch corridor types mid-stay?", a: "If we have availability, yes. Ask at the front desk — we've moved plenty of guests from an exterior to an interior room after one noisy night." },
];

export default function InteriorVsExterior() {
  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/photos`).then((r) => {
      if (alive) setPhotos(r.data.photos || []);
    });
    return () => { alive = false; };
  }, []);

  const hero = useMemo(
    () => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-exterior-facade"),
    [photos],
  );
  const interiorPhoto = useMemo(
    () => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-lodging-interior"),
    [photos],
  );
  const exteriorPhoto = useMemo(
    () => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-exterior-driveway"),
    [photos],
  );

  const canonical = "https://westbridgehotels.com/rooms/interior-vs-exterior";
  useSeo({
    title: "Interior vs Exterior Rooms — A Guide (Westbridge Inn, Centerville, Iowa)",
    description:
      "22 interior-corridor rooms or 30 exterior-access rooms — which is right for your trip? A guide by traveler type, honest trade-offs, and a comparison table.",
    canonical,
    image: hero ? `${window.location.origin}${hero.jpg}` : undefined,
    jsonLd: [
      buildBreadcrumbLd([
        { label: "Rooms", to: "/rooms" },
        { label: "Interior vs Exterior", to: "/rooms/interior-vs-exterior" },
      ]),
      buildFaqLd(GUIDE_FAQS),
    ],
  });

  return (
    <main id="main" data-testid="interior-vs-exterior-page">
      <PageHero
        eyebrow="A guide"
        title="Interior corridor or park-at-your-door?"
        intro="Twenty-two of our rooms open to an interior hallway. Thirty open to the parking lot. The one that fits your trip depends on what you're driving, who you're driving with, and how quiet you like your mornings."
        photo={hero}
        breadcrumbs={[
          { label: "Rooms", to: "/rooms" },
          { label: "Interior vs Exterior", to: "/rooms/interior-vs-exterior" },
        ]}
        testId="corridor-guide-hero"
      />

      {/* Two-column intro cards */}
      <section className="bg-cream py-16 md:py-24">
        <div className="container-app grid md:grid-cols-2 gap-6 md:gap-10">
          <article className="bg-cream-100 border border-tan overflow-hidden" data-testid="corridor-guide-interior">
            <div className="block w-full aspect-[16/10] overflow-hidden bg-forest-800">
              {interiorPhoto && (
                <Picture
                  photo={interiorPhoto}
                  className="block w-full h-full"
                  imgClassName="w-full h-full object-cover"
                  sizes="(min-width: 768px) 45vw, 100vw"
                />
              )}
            </div>
            <div className="p-8 md:p-10">
              <div className="flex items-center gap-3 mb-4">
                <Building2 size={22} className="text-brass" strokeWidth={1.5} />
                <p className="eyebrow">Interior corridor · 22 rooms</p>
              </div>
              <h2 className="font-display text-2xl md:text-3xl text-forest-900 mb-4">
                Quieter, warmer, close to breakfast and the pool.
              </h2>
              <p className="text-ink/80 leading-relaxed mb-5">
                Enter through the lobby, walk a climate-controlled hallway to your door. Ideal for
                light sleepers, families with kids, winter travelers, and anyone who wants a short
                indoor walk to the breakfast room.
              </p>
              <p className="eyebrow mb-3 text-forest-800">Best for</p>
              <ul className="text-sm text-ink/85 space-y-1 mb-6">
                <li>· Families with young kids</li>
                <li>· Light sleepers</li>
                <li>· Winter travelers</li>
                <li>· Anyone using the pool a lot</li>
                <li>· Longer business stays</li>
              </ul>
              <Link
                to="/rooms/king"
                className="btn-ghost-dark text-sm"
                data-testid="corridor-guide-see-interior"
              >
                See an interior room →
              </Link>
            </div>
          </article>

          <article className="bg-forest-900 text-cream border border-forest-700/60 overflow-hidden" data-testid="corridor-guide-exterior">
            <div className="block w-full aspect-[16/10] overflow-hidden bg-forest-800">
              {exteriorPhoto && (
                <Picture
                  photo={exteriorPhoto}
                  className="block w-full h-full"
                  imgClassName="w-full h-full object-cover"
                  sizes="(min-width: 768px) 45vw, 100vw"
                />
              )}
            </div>
            <div className="p-8 md:p-10">
              <div className="flex items-center gap-3 mb-4">
                <DoorOpen size={22} className="text-brass-300" strokeWidth={1.5} />
                <p className="eyebrow text-brass-300">Exterior access · 30 rooms</p>
              </div>
              <h2 className="font-display text-2xl md:text-3xl mb-4">
                Park at your door. Load, unload, go.
              </h2>
              <p className="text-cream/85 leading-relaxed mb-5">
                Open the door and you're inside. Perfect for truckers, construction crews, hunters,
                anglers, and pet owners — no lobby detour on a bathroom break or a 5 a.m. departure.
              </p>
              <p className="eyebrow mb-3 text-brass-300">Best for</p>
              <ul className="text-sm text-cream/85 space-y-1 mb-6">
                <li>· Truck drivers</li>
                <li>· Construction crews</li>
                <li>· Pet owners</li>
                <li>· Hunters &amp; anglers</li>
                <li>· Late arrivals &amp; early departures</li>
              </ul>
              <Link
                to="/rooms/exterior-access"
                className="btn-outline text-sm"
                data-testid="corridor-guide-see-exterior"
              >
                See an exterior room →
              </Link>
            </div>
          </article>
        </div>
      </section>

      {/* Comparison table */}
      <section className="bg-cream-100 py-16 md:py-24 paper-grain" data-testid="corridor-guide-table">
        <div className="container-app relative">
          <div className="max-w-xl mb-10">
            <p className="eyebrow mb-3">Side by side</p>
            <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-[1.05]">
              An honest comparison.
            </h2>
          </div>
          <div className="overflow-x-auto -mx-5 md:mx-0 px-5 md:px-0 no-scrollbar">
            <table className="w-full min-w-[720px] text-left border-collapse">
              <thead>
                <tr>
                  <th className="py-4 pr-6 text-xs uppercase tracking-[0.18em] text-ink/50 border-b border-tan font-medium w-1/4">
                    &nbsp;
                  </th>
                  <th className="py-4 pr-6 text-forest-900 border-b border-tan font-display text-lg md:text-xl">
                    <Building2 size={16} className="inline mr-2 text-brass" /> Interior
                  </th>
                  <th className="py-4 text-forest-900 border-b border-tan font-display text-lg md:text-xl">
                    <DoorOpen size={16} className="inline mr-2 text-brass" /> Exterior
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map(([label, a, b]) => (
                  <tr key={label} className="align-top">
                    <th className="py-4 pr-6 text-xs uppercase tracking-[0.14em] text-ink/60 border-b border-tan-200 font-medium">
                      {label}
                    </th>
                    <td className="py-4 pr-6 text-ink/80 border-b border-tan-200 text-[0.95rem] leading-relaxed">
                      {a}
                    </td>
                    <td className="py-4 text-ink/80 border-b border-tan-200 text-[0.95rem] leading-relaxed">
                      {b}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Persona picker */}
      <section className="bg-cream py-16 md:py-24">
        <div className="container-app">
          <div className="max-w-xl mb-10">
            <p className="eyebrow mb-3">Which should you pick?</p>
            <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-[1.05]">
              A shortcut by traveler type.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <PersonaTile icon={Users} label="Family" pick="Interior" why="Kids reach the pool and breakfast without walking outside. Quieter halls at bedtime." to="/rooms/double-queen" />
            <PersonaTile icon={Briefcase} label="Business (light sleeper)" pick="Interior" why="Quieter halls, warmer in winter, quick pool decompression at end of day." to="/rooms/king" />
            <PersonaTile icon={Truck} label="Truck driver" pick="Exterior" why="Park at your door. Load, unload, and depart before sunrise without a lobby loop." to="/rooms/exterior-access" />
            <PersonaTile icon={PawPrint} label="Traveling with a pet" pick="Exterior" why="Bathroom breaks in seconds. Straight out the door — no lobby detour." to="/rooms/exterior-access" />
            <PersonaTile icon={Thermometer} label="Winter traveler" pick="Interior" why="Climate-controlled hallways. No snow tracked into your room every time the door opens." to="/rooms/king-jacuzzi-suite" />
            <PersonaTile icon={Sun} label="Summer lake weekend" pick="Either" why="Interior sleeps quieter after a lake day. Exterior gets you and your gear out at 5 a.m. for the good bite." to="/rooms" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-cream-100 py-16 md:py-24 paper-grain" data-testid="corridor-guide-faq">
        <div className="container-app relative grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="eyebrow mb-3">Common questions</p>
            <h2 className="font-display font-light text-2xl md:text-4xl text-forest-900 tracking-tight leading-tight">
              Still on the fence?
            </h2>
          </div>
          <div className="md:col-span-8">
            <FaqAccordion items={GUIDE_FAQS} testIdPrefix="corridor-guide-faq" />
          </div>
        </div>
      </section>

      <CtaBlock
        eyebrow="Book with the corridor you want"
        title="Tell us at booking — we'll place you accordingly."
        body="Call the front desk if you have a strong preference, need a specific accommodation, or want to book two rooms next to each other."
      />
    </main>
  );
}

function PersonaTile({ icon: Icon, label, pick, why, to }) {
  const preferInterior = pick === "Interior";
  const preferExterior = pick === "Exterior";
  return (
    <article className="bg-cream-50 border border-tan p-7 flex flex-col">
      <div className="flex items-center gap-3 mb-3">
        <Icon size={20} className="text-brass" strokeWidth={1.5} />
        <p className="text-xs uppercase tracking-[0.18em] text-forest-800 font-medium">{label}</p>
      </div>
      <p className={`font-display text-2xl mb-3 ${preferInterior ? "text-forest-900" : preferExterior ? "text-forest-900" : "text-brass"}`}>
        Pick: <span className={preferInterior ? "text-forest" : preferExterior ? "text-forest" : "text-brass"}>{pick}</span>
      </p>
      <p className="text-ink/80 text-sm leading-relaxed grow">{why}</p>
      <Link to={to} className="mt-5 inline-flex items-center gap-1 text-sm text-forest-900 brass-underline">
        See recommended room →
      </Link>
    </article>
  );
}
